import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import type {
  CreateCycleRequestDto,
  CycleHistoryEntryDto,
  CycleSnapshotDto,
  UpdateCycleRequestDto,
} from '../types';

export function useActiveCycle() {
  return useQuery({
    queryKey: ['plan', 'active'],
    queryFn: async ({ signal }) => {
      const resp = await fetch('/api/cycle/active', { credentials: 'include', signal });
      if (resp.status === 404) return null;
      if (!resp.ok) throw new Error(`/api/cycle/active failed: ${resp.status}`);
      return (await resp.json()) as CycleSnapshotDto;
    },
    retry: false,
  });
}

export function useCycleHistory() {
  return useQuery({
    queryKey: ['plan', 'history'],
    queryFn: ({ signal }) =>
      api.get<CycleHistoryEntryDto[]>('/api/cycle/history', { signal }),
  });
}

function invalidatePlanAndToday(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['plan'] });
  void qc.invalidateQueries({ queryKey: ['today'] });
}

export function useCreateCycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCycleRequestDto) => api.post<CycleSnapshotDto>('/api/cycle', body),
    onSuccess: () => invalidatePlanAndToday(qc),
  });
}

export function useUpdateCycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateCycleRequestDto }) =>
      api.put<CycleSnapshotDto>(`/api/cycle/${encodeURIComponent(id)}`, body),
    onSuccess: () => invalidatePlanAndToday(qc),
  });
}

export function useEndCycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<unknown>(`/api/cycle/${encodeURIComponent(id)}/end`, {}),
    onSuccess: () => invalidatePlanAndToday(qc),
  });
}

export function useDeleteCycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/cycle/${encodeURIComponent(id)}`),
    onSuccess: () => invalidatePlanAndToday(qc),
  });
}
